import {Howl} from "howler";
import {config} from "../../../config";
import {AceCard} from "../../AceCard";
import {BlackjackAnimation} from "../../BlackjackAnimation";
import {Card} from "../../Card";
import {CardsHandAnimation} from "../../CardsHandAnimation";
import data from "../../configs/data";
import {HandPosition} from "../../HandPosition";
import {ChipsStack} from "../../userInterface/ChipsStack";
import {HandValueCounter} from "../../userInterface/HandValueCounter";
import {Utils} from "../../Utils";
import {WinInfoAnimation} from "../../WinInfoAnimation";

export class BlackjackHand extends PIXI.Container {

    private readonly BLACKJACK_ANIMATION_POSITION = new PIXI.Point(0, 10);
    private readonly NO_SPLIT_CHIPS_STACK_POSITION = new PIXI.Point(0, 110);
    private readonly SPLIT_CHIPS_STACK_POSITION = new PIXI.Point(0, 85);
    private readonly WINNING_CHIPS_STACK_POSITION = new PIXI.Point(50, 100);
    private readonly SPLIT_1_WINNING_CHIPS_STACK_POSITION = new PIXI.Point(50, 85);

    private readonly LEFT_HAND_INSURANCE_CHIPS_STACK_POSITION = new PIXI.Point(90, -10);
    private readonly CENTER_HAND_INSURANCE_CHIPS_STACK_POSITION = new PIXI.Point(90, -10);
    private readonly RIGHT_HAND_INSURANCE_CHIPS_STACK_POSITION = new PIXI.Point(-75, -10);

    private readonly VALUE_COUNTER_DEALER_POSITION = new PIXI.Point(50, -75);
    private readonly VALUE_COUNTER_BASIC_POSITION = new PIXI.Point(50, -57);

    private readonly WIN_INFO_ANIMATION_BASIC_POSITION = new PIXI.Point(-5, 19);

    private readonly tokensValuesPaths = [
        data.images.casinoChipValue100,
        data.images.casinoChipValue25,
        data.images.casinoChipValue10,
        data.images.casinoChipValue5,
        data.images.casinoChipValue2_5,
        data.images.casinoChipValue1,
        data.images.casinoChipValue05,
        data.images.casinoChipValue01,
    ];
    private readonly tokensValues = [50, 25, 10, 5, 2.5, 1, 0.5, 0.1];

    private cards: Array<Card> = [];
    private handInsured = false;
    private handValue: number = 0;
    private alternativeHandValue: number = 0;

    private cardsHandAnimation: CardsHandAnimation;
    private blackjackAnimation: BlackjackAnimation;
    private chipsStack: ChipsStack;
    private winningChipsStack: ChipsStack;
    private insuranceChipsStack: ChipsStack;
    private handValueCounter: HandValueCounter;
    private winInfoAnimation: WinInfoAnimation;
    private doubled = false;

    private blackjackSound: Howl;

    constructor(private betAmount: number, private handPosition: HandPosition,
                private splitOnePosition?: number, private splitTwoPosition?: number,
                private splitNumber?: number) {
        super();

        if (config.extremelySmallBets) {
            this.tokensValuesPaths = [
                data.images.extremelySmallBetsCasinoChipValue05,
                data.images.extremelySmallBetsCasinoChipValue025,
                data.images.extremelySmallBetsCasinoChipValue01,
                data.images.extremelySmallBetsCasinoChipValue005,
                data.images.extremelySmallBetsCasinoChipValue0025,
                data.images.extremelySmallBetsCasinoChipValue001,
                data.images.extremelySmallBetsCasinoChipValue0005,
                data.images.extremelySmallBetsCasinoChipValue0001,
            ];
            this.tokensValues = [0.5, 0.25, 0.1, 0.05, 0.025, 0.01, 0.005, 0.001];
        } else if (config.smallBets) {
            this.tokensValuesPaths = [
                data.images.smallBetsCasinoChipValue5,
                data.images.smallBetsCasinoChipValue2_5,
                data.images.smallBetsCasinoChipValue1,
                data.images.smallBetsCasinoChipValue05,
                data.images.smallBetsCasinoChipValue025,
                data.images.smallBetsCasinoChipValue01,
                data.images.smallBetsCasinoChipValue005,
                data.images.smallBetsCasinoChipValue001,
            ];
            this.tokensValues = [5, 2.5, 1, 0.5, 0.25, 0.1, 0.05, 0.01];
        }

        this.createAndAddCardsHandAnimation();
        this.createAndAddChipsStack(handPosition, splitOnePosition, splitTwoPosition);
        this.createAndAddInsuranceChipsStack(handPosition);
        this.createAndAddWinningChipsStack(handPosition, splitOnePosition);
        this.createAndAddHandValueCounter(handPosition);
        this.createAndAddBlackjackAnimation();
        this.createAndAddWinInfoAnimation();

        this.blackjackSound = new Howl({
            src: [data.audios.blackjack],
            volume: 0.75,
        });
    }

    public getBetAmount(): number {
        return this.betAmount;
    }

    public async brailCards(): Promise<void> {
        const wasVisibleBefore = this.handValueCounter.visible;
        this.handValueCounter.visible = false;
        await this.cardsHandAnimation.playBrailCards();
        this.resetHandValueCounterPosition();
        this.handValueCounter.visible = wasVisibleBefore;
    }

    public setBetAmount(value: number): void {
        this.betAmount = value;
        this.chipsStack.setStack(value);
    }

    public getHandPosition(): HandPosition {
        return this.handPosition;
    }

    public insure(): void {
        this.handInsured = true;
        this.insuranceChipsStack.setStack(Utils.roundWithPrecision(this.betAmount / 2, Utils.getBetPrecision()));
    }

    public removeInsuranceStack(): void {
        this.insuranceChipsStack.setStack(0);
    }

    public isHandInsured(): boolean {
        return this.handInsured;
    }

    public getSplitNumber(): number {
        return this.splitNumber;
    }

    public getSplitOnePosition(): number {
        return this.splitOnePosition;
    }

    public getSplitTwoPosition(): number {
        return this.splitTwoPosition;
    }

    public double(): void {
        this.doubled = true;
        this.chipsStack.setStack(this.betAmount * 2);
    }

    public isDoubled(): boolean {
        return this.doubled;
    }

    public addCard(card: Card): void {
        this.cards.push(card);
        this.cardsHandAnimation.addCard(card);
        this.recalculateCurrentHandValue();
        this.recalculateAlternativeHandValue();
        this.handValueCounter.setValue(this.handValue, this.alternativeHandValue);
        this.handValueCounter.visible = true;
        this.resetHandValueCounterPosition();
        if (this.isBlackjack()) {
            this.blackjackAnimation.show();
        }
        if (this.isBlackjack() && this.handPosition !== HandPosition.DEALER) {
            this.blackjackSound.play();
        }
        if (this.doesBust()) {
            this.playBustInfoLabel();
        }
    }

    public setCards(cards: Array<Card>): void {
        this.cardsHandAnimation.removeAllCardsFromHand();
        this.cards = cards;
        for (let i = 0; i < this.cards.length; i++) {
            this.cardsHandAnimation.addCard(this.cards[i]);
        }
        this.recalculateCurrentHandValue();
        this.recalculateAlternativeHandValue();
        this.resetHandValueCounterPosition();
        if (this.isBlackjack()) {
            this.blackjackAnimation.show();
        }
        if (this.doesBust()) {
            this.playBustInfoLabel();
        }
    }

    public getCards(): Array<Card> {
        return this.cards;
    }

    public setWinOnChipsStacksStacks(value: number): void {
        let winStackValue = value;
        winStackValue -= this.chipsStack.getStackValue();
        winStackValue -= this.insuranceChipsStack.getStackValue();
        this.winningChipsStack.setStack(winStackValue);
    }

    public showHandValueCounter(): void {
        this.handValueCounter.visible = true;
    }

    public hideHandValueCounter(): void {
        this.handValueCounter.visible = false;
    }

    public getNextCardGlobalPosition(): PIXI.Point {
        const globalHandPosition = this.cardsHandAnimation.getGlobalPosition();
        const nextCardLocalPosition: PIXI.Point = this.cardsHandAnimation.getNextCardPosition();
        return new PIXI.Point(globalHandPosition.x + nextCardLocalPosition.x,
            globalHandPosition.y + nextCardLocalPosition.y);
    }

    public doesBust(): boolean {
        return (this.handValue > 21 && this.alternativeHandValue > 21);
    }

    public isBlackjack(): boolean {
        return (this.getCards().length === 2 &&
            this.getSplitNumber() === 0 &&
            (this.handValue === 21 || this.alternativeHandValue === 21));
    }

    public getFinalHandValue(): number {
        const playerHandAlternativeValue = this.alternativeHandValue <= 21 ? this.alternativeHandValue : 0;
        const playerHandValue = this.handValue <= 21 ? this.handValue : 0;
        const preFinalValue = playerHandAlternativeValue > playerHandValue ?
            playerHandAlternativeValue : playerHandValue;
        if (preFinalValue === 0) {
            return this.alternativeHandValue < this.handValue ? this.alternativeHandValue : this.handValue;
        }
        return preFinalValue;
    }

    public playBustInfoLabel(): void {
        this.blackjackAnimation.hide();
        this.resetWinInfoAnimationPosition();
        this.winInfoAnimation.playBust();
    }

    public playNoWinInfoLabel(): void {
        this.blackjackAnimation.hide();
        this.resetWinInfoAnimationPosition();
        this.winInfoAnimation.playNoWin();
    }

    public playPushInfoLabel(winAmount: number): void {
        this.blackjackAnimation.hide();
        this.resetWinInfoAnimationPosition();
        this.winInfoAnimation.playPush(winAmount);
    }

    public playYouWinInfoLabel(winAmount: number): void {
        this.blackjackAnimation.hide();
        this.resetWinInfoAnimationPosition();
        this.winInfoAnimation.playYouWin(winAmount);
    }

    public clear(): void {
        this.setCards([]);
        this.betAmount = 0;
        this.handInsured = false;
        this.doubled = false;
        this.cardsHandAnimation.removeAllCardsFromHand();
        this.chipsStack.setStack(0);
        this.insuranceChipsStack.setStack(0);
        this.winningChipsStack.setStack(0);
        this.handValueCounter.visible = false;
        this.blackjackAnimation.hide();
        this.winInfoAnimation.hide();
        this.cardsHandAnimation.playIdleAllCards();
    }

    private resetWinInfoAnimationPosition(): void {
        let positionX = this.WIN_INFO_ANIMATION_BASIC_POSITION.x;
        if (this.cards.length < 7) {
            positionX += this.cards.length * 12 / 2;
        } else {
            positionX += 6 * 12 / 2 + (this.cards.length - 6);
        }
        this.winInfoAnimation.position = new PIXI.Point(positionX, this.WIN_INFO_ANIMATION_BASIC_POSITION.y);
    }

    private resetHandValueCounterPosition(): void {
        const getCurrentCardPosition = this.cardsHandAnimation.getCurrentCardPosition();
        if (this.handPosition === HandPosition.DEALER) {
            this.handValueCounter.position = new PIXI.Point(
                getCurrentCardPosition.x + this.VALUE_COUNTER_DEALER_POSITION.x,
                getCurrentCardPosition.y + this.VALUE_COUNTER_DEALER_POSITION.y);
        } else {
            this.handValueCounter.position = new PIXI.Point(
                getCurrentCardPosition.x + this.VALUE_COUNTER_BASIC_POSITION.x,
                getCurrentCardPosition.y + this.VALUE_COUNTER_BASIC_POSITION.y);
        }
    }

    private recalculateCurrentHandValue(): void {
        let currentValue = 0, aceCardFound = false;
        for (let i = 0; i < this.cards.length; i++) {
            if (this.cards[i] instanceof AceCard && !aceCardFound) {
                aceCardFound = true;
                currentValue += (this.cards[i] as AceCard).getAlternativeValue();
            } else {
                currentValue += this.cards[i].getValue();
            }
        }
        this.handValue = currentValue;
    }

    private recalculateAlternativeHandValue(): void {
        let currentValue = 0;
        for (let i = 0; i < this.cards.length; i++) {
            currentValue += this.cards[i].getValue();
        }
        this.alternativeHandValue = currentValue;
    }

    private createAndAddCardsHandAnimation(): void {
        this.cardsHandAnimation = new CardsHandAnimation();
        this.addChild(this.cardsHandAnimation);
    }

    private createAndAddChipsStack(handPosition: HandPosition,
                                   splitOnePosition?: number, splitTwoPosition?: number): void {
        this.chipsStack = new ChipsStack(this.tokensValuesPaths, this.tokensValues);
        if (splitOnePosition === undefined) {
            this.chipsStack.position = this.NO_SPLIT_CHIPS_STACK_POSITION;
        } else if (splitTwoPosition === undefined) {
            this.chipsStack.position = this.SPLIT_CHIPS_STACK_POSITION;
        } else if (handPosition === HandPosition.PLAYER_CENTER && splitOnePosition === 1 && splitTwoPosition === 0) {
            this.chipsStack.position = this.SPLIT_CHIPS_STACK_POSITION;
        } else {
            this.chipsStack.position = this.SPLIT_CHIPS_STACK_POSITION;
        }
        this.addChild(this.chipsStack);
    }

    private createAndAddInsuranceChipsStack(handPosition: HandPosition): void {
        this.insuranceChipsStack = new ChipsStack(this.tokensValuesPaths, this.tokensValues);
        if (handPosition === HandPosition.PLAYER_LEFT) {
            this.insuranceChipsStack.position = this.LEFT_HAND_INSURANCE_CHIPS_STACK_POSITION;
        } else if (handPosition === HandPosition.PLAYER_CENTER) {
            this.insuranceChipsStack.position = this.CENTER_HAND_INSURANCE_CHIPS_STACK_POSITION;
        } else if (handPosition === HandPosition.PLAYER_RIGHT) {
            this.insuranceChipsStack.position = this.RIGHT_HAND_INSURANCE_CHIPS_STACK_POSITION;
        }
        this.addChild(this.insuranceChipsStack);
    }

    private createAndAddWinningChipsStack(handPosition: HandPosition, splitOnePosition?: number): void {
        this.winningChipsStack = new ChipsStack(this.tokensValuesPaths, this.tokensValues);
        if (splitOnePosition === undefined) {
            this.winningChipsStack.position = this.WINNING_CHIPS_STACK_POSITION;
        } else {
            this.winningChipsStack.position = this.SPLIT_1_WINNING_CHIPS_STACK_POSITION;
        }
        this.addChild(this.winningChipsStack);
    }

    private createAndAddBlackjackAnimation(): void {
        this.blackjackAnimation = new BlackjackAnimation();
        this.blackjackAnimation.position = this.BLACKJACK_ANIMATION_POSITION;
        this.addChild(this.blackjackAnimation);
    }

    private createAndAddHandValueCounter(handPosition: HandPosition): void {
        this.handValueCounter = new HandValueCounter();
        if (handPosition === HandPosition.DEALER) {
            this.handValueCounter.position = this.VALUE_COUNTER_DEALER_POSITION;
        } else {
            this.handValueCounter.position = this.VALUE_COUNTER_BASIC_POSITION;
        }
        this.handValueCounter.visible = false;
        this.addChild(this.handValueCounter);
    }

    private createAndAddWinInfoAnimation(): void {
        this.winInfoAnimation = new WinInfoAnimation();
        this.winInfoAnimation.position = this.WIN_INFO_ANIMATION_BASIC_POSITION;
        this.addChild(this.winInfoAnimation);
    }
}



// WEBPACK FOOTER //
// ./src/app/api/serverResponseClasses/BlackjackHand.ts